import React, { useEffect, useMemo, useRef, useState } from 'react';
import useBoardContentWidgetData from '../../../hooks/useBoardContentWidgetData';
import { MODAL, POPUP } from '../../../../../redux/types';
import { useDispatch } from 'react-redux';
import useTranslate from '../../../../../common/hooks/useTranslate';
import useBoardContentObject from '../../../hooks/useBoardContentObject';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { useParams } from 'react-router-dom';
import useClickOutside from '../../../../../common/hooks/useClickOutside';
import { exportImage, exportPDF, exportSVG } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { Rect } from '@progress/kendo-drawing/geometry';
import useBoardContentWidget from '../../../hooks/useBoardContentWidget';
import useAxisChart from '../../../hooks/useAxisChart';
import useCircleChart from '../../../hooks/useCircleChart';

import { WidgetProps } from '../../../types/IWidgetProps';
import { ICurrency } from '../../../types/ICurrency';
import { IBoardObjectNode } from '../../../types/IBoardObjectNode';
import { IChartSetup } from './types/IChartSetup';
import { IBoardMeasure } from '../../../types/IBoardMeasure';
import { IWidgetData } from '../../../types/IWidgetData';
import { IBoardNote } from '../../../types/IBoardNote';
import { IChartBreadcrumb } from './types/IChartBreadcrumb';
import { IChartSerie } from './types/IChartSerie';
import { IChartCashflow } from './types/IChartCashflow';
import { CategoryAxis, exportVisual } from '@progress/kendo-react-charts';
import { IDrilldownInfo } from './types/IDrilldownInfo';
import { ISeriesInfo } from './types/ISeriesInfo';
import { IGridData } from './types/IGridData';

import WidgetWrapper from '../widgetWrapper';
import Picker from '../../../../ui/picker/picker';
import ContentLoading from '../../../../ui/loaders/contentLoading/contentLoading';
import Icon from '../../../../ui/icon/icon';
import { ExcelExport, ExcelExportColumn, ExcelExportColumnGroup } from '@progress/kendo-react-excel-export';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import ChartRender from './chartRender/chartRender';
import NumericCell from '../../cells/numericCell/numericCell';
import ChartCashflowCell from './cells/chartCashflowCell/chartCashflowCell';
import { Popup } from '@progress/kendo-react-popup';

import './chartWidget.scss';

const initialSort: Array<SortDescriptor> = [{ field: 'periodName', dir: 'asc' }];

const ChartWidget: React.FC<WidgetProps> = ({
                                              widget,
                                              selectedPeriod,
                                              selectedObject,
                                              selectedCurrency,
                                              scenarios,
                                              boardData,
                                              customisation,
                                              notes,
                                              moveWidget,
                                              removeWidget,
                                              handleLink,
                                              homeContent,
                                              content,
                                              widgetView,
                                              handlePinWidget,
                                              handleUnpinWidget,
                                              handleUnpinTrigger,
                                              unpinId,
                                            }) => {
  const [chartData, setChartData] = useState<IWidgetData[]>([]);
  const [chartSetup, setChartSetup] = useState<IChartSetup>();
  const [chartNotes, setChartNotes] = useState<IBoardNote[]>([]);
  const [widgetCurrencies, setWidgetCurrencies] = useState<ICurrency[]>([]);
  const [localCurrency, setLocalCurrency] = useState<ICurrency>();
  const [nodes, setNodes] = useState<IBoardObjectNode[]>([]);
  const [nodeGroups, setNodeGroups] = useState<IBoardObjectNode[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [valueAxes, setValueAxes] = useState<CategoryAxis[]>([]);
  const [series, setSeries] = useState<IChartSerie[]>([]);
  const [gridView, setGridView] = useState<boolean>(false);
  const [gridData, setGridData] = useState<IGridData[]>([]);
  const [gridOptions, setGridOptions] = useState<any>([]);
  const [focusedNode, setFocusedNode] = useState<any>();
  const [breadcrumbs, setBreadcrumbs] = useState<IChartBreadcrumb[]>([{ id: 0, name: '', level: 0, node: null }]);
  const [chartItemPicker, setChartItemPicker] = useState<any>(null);
  const [popupVisible, setPopUpVisible] = useState<boolean>(false);
  const [drillDownData, setDrillDownData] = useState<IDrilldownInfo[] | null>(null);
  const [cashFlow, setCashFlow] = useState<IChartCashflow | null>(null);
  const [position, setPosition] = useState<any>({ left: 0, top: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  const [expand, setExpand] = useState<boolean>();
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort);

  const {
    defineNodes,
    generateCurrencies,
    fetchData,
    fetchNodeData,
    defineNodesForCategoryTypeLevel,
    defineNodesForParent,
  } = useBoardContentWidgetData();
  const { findNode } = useBoardContentObject();
  const { widgetSetup } = useBoardContentWidget();
  const {
    generateCategoriesSingle,
    generateSeriesSingle,
    generateValueAxes,
    generateCategories,
    generateSeries,
  } = useAxisChart();
  const {
    generateCircleData,
  } = useCircleChart();
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const params = useParams();
  const chartRef = useRef<any>(null);
  const gridRef = useRef<any>(null);
  const exportRef = useRef<any>(null);
  const popupWrapperRef = useRef<any>(null);

  const pickerListChart = [
    {
      name: t('CONTEXT_MENU.EXPORT_SVG'),
      icon: 'far fa-file-image',
      type: 'svg',
    },
    {
      name: t('CONTEXT_MENU.EXPORT_IMAGE'),
      icon: 'far fa-file-image',
      type: 'image',
    },
    {
      name: t('CONTEXT_MENU.EXPORT_PDF'),
      icon: 'far fa-file-pdf',
      type: 'pdf',
    },
    {
      name: t('CONTEXT_MENU.TOGGLE_VIEW'),
      icon: 'far fa-square-sliders-vertical',
      type: 'toggle',
    },
  ];

  const pickerListGrid = [
    {
      name: t('CONTEXT_MENU.EXPORT_XLSX'),
      icon: 'far fa-file-excel',
      type: 'excel',
    },
    {
      name: t('CONTEXT_MENU.TOGGLE_VIEW'),
      icon: 'far fa-square-sliders-vertical',
      type: 'toggle',
    },
  ];

  const mm = (val) => val * 2.8347;
  const PAGE_RECT = new Rect([0, 0], [mm(297), mm(210)]);

  const handleSortChange = (e) => {
    setSort(e.sort);
  };

  const seriesData = (e): ISeriesInfo | undefined => {
    let ref = e.series.data[0].lookupRef;
    if (!ref) return;

    let info: ISeriesInfo = {
      nodeId: null,
      measureId: null,
      period: null,
      amount: null,
      value: null,
    };

    if (chartSetup?.singlePeriod) {
      info.nodeId = e.dataItem.nodeId;
      info.measureId = parseInt(ref.measureId);
      info.amount = e.dataItem.value * (ref.amountsIn || 1);
      info.value = e.dataItem.value;
      info.period = selectedPeriod || null;
    } else {
      info.nodeId = parseInt(ref.nodeId);
      info.measureId = parseInt(ref.measureId);
      info.amount = e.dataItem.amount * ref.amountsIn;
      info.value = e.dataItem;
      info.period = boardData?.timePeriods?.find((p) => p.name === e.category) || null;
    }

    return info;
  };

  const drillDownInfo = (nodeId: number): IDrilldownInfo[] => {
    let drillDownInfo: IDrilldownInfo[] = [];

    if (chartSetup?.enableDrillDown) {
      let node = findNode(selectedObject?.nodes || [], nodeId);

      if (node?.childNodes && node.childNodes.length > 0) {
        let hasNonGroups = false;

        node.childNodes.forEach((child) => {
          if (child.isGroup)
            drillDownInfo.push({ id: child.id, name: child.name });
          else
            hasNonGroups = true;
        });

        if (hasNonGroups)
          drillDownInfo.push({ id: nodeId, name: undefined });
      }
    }

    return drillDownInfo;
  };

  const initChart = (chartData) => {
    if (chartData && chartSetup) {
      switch (chartSetup.type) {
        case 'line':
        case 'bar':
        case 'column':
        case 'area':
        case 'marker':
        case 'funnel':
          if (chartSetup.singlePeriod) {
            setCategories(generateCategoriesSingle(focusedNode ? focusedNode.childNodes : nodes, localCurrency?.currency === '_'));
            const {
              series,
              gridData,
            } = generateSeriesSingle(chartSetup, boardData?.measures || [], chartData, selectedPeriod);
            setSeries(series);
            setGridData(gridData || []);
            setValueAxes(generateValueAxes(chartSetup));
          } else {
            setCategories(generateCategories(boardData?.timePeriods, chartData));
            const {
              series,
              gridData,
            } = generateSeries(chartSetup, focusedNode ? focusedNode.childNodes : nodes, boardData?.measures || [], chartData, localCurrency?.currency === '_');
            setSeries(series);
            setGridData(gridData || []);
            setValueAxes(generateValueAxes(chartSetup));
          }
          break;
        case 'pie':
        case 'donut':
          //@ts-ignore
          const {serie,gridData} = generateCircleData(focusedNode ? focusedNode.childNodes : nodes, chartData, chartSetup, boardData?.measures || [], selectedPeriod);
          setSeries(serie);
          setGridData(gridData || []);
          break;
        default:
          console.log(`Setup type unknown ${chartSetup.type}.`);
      }
    }
  };

  const onImageExportClick = () => {
    const chartVisual = exportVisual(chartRef.current);

    if (chartVisual) {
      exportImage(chartVisual).then((dataURI) => saveAs(dataURI, widget.name + '.png'));
    }
  };

  const onPDFExportClick = () => {
    const chartVisual = exportVisual(chartRef.current, {
      width: PAGE_RECT.size.width,
    });

    if (chartVisual) {
      exportPDF(chartVisual, {
        landscape: true,
      }).then((dataURI) => saveAs(dataURI, widget.name + '.pdf'));
    }
  };

  const onSVGExportClick = () => {
    const chartVisual = exportVisual(chartRef.current);

    if (chartVisual) {
      exportSVG(chartVisual).then((dataURI) => saveAs(dataURI, widget.name + '.svg'));
    }
  };

  const onExcelExport = () => {
    if (exportRef.current !== null) {
      let exportColumns: any[] = [];
      gridRef.current && gridRef.current.columns && gridRef.current.columns.forEach((column) => {
        if (column.children && column.children.length > 0) {
          column.children.forEach((child) => {
            exportColumns.push({
              field: child.field,
              title: `${child.title} ${column.title}`,
            });
          });
        } else {
          exportColumns.push({
            field: column.field,
            title: column.title,
          });
        }
      });
      exportRef.current.save(orderBy(gridData, sort), exportColumns);
    }
  };

  const handleSeriesClickAxes = (e): IChartCashflow | null => {
    let ref = e.series.data[0].lookupRef;
    if (!ref)
      return null;

    let info: ISeriesInfo | undefined = seriesData(e);

    if (info && info.nodeId && info.measureId && info.period) {
      //@ts-ignore
      let value = chartData?.find((dataItem) => (
        dataItem['nodeId'] === info?.nodeId && dataItem['measureId'] === info?.measureId && dataItem['periodId'] === (info?.period && info?.period['id'])
      ));

      let measure = boardData?.measures?.find((m) => m.id === info?.measureId);

      let nodesForSearch = focusedNode?.childNodes || nodes;
      let node = nodesForSearch?.find((n) => n.id === info?.nodeId);

      if (value) {
        setDrillDownData(drillDownInfo(node?.id || 0));

        return {
          nodeId: node?.id,
          scenarioId: node?.scenarioId,
          valueId: value.id,
          value: info.value,
          measureId: info.measureId,
          viewCashflows: ref.viewCashflows,
          currency: node?.currency,
          period: info.period,
          valueInfo: {
            name: node?.name,
            measure: measure,
            amount: info.amount,
            scale: ref.scale,
          },
        };
      }
    }

    return null;
  };

  const columnHasValues = (column: any) => {
    for (let j = 0; j < gridData.length; j++) {
      let row = gridData[j];
      if (row[column.field] !== undefined && row[column.field] !== null) {
        return true;
      }
    }
    return false;
  };

  const clearEmptyGridColumns = (columns: any) => {
    for (let i = columns.length - 1; i > 0; i--) {
      let column = columns[i];

      if (column.columns && column.columns.length > 0) {
        for (let j = column.columns.length - 1; j > -1; j--) {
          let subCol = column.columns[j];
          if (!columnHasValues(subCol)) column.columns.splice(j, 1);
        }
      } else {
        if (!columnHasValues(column)) columns.splice(i, 1);
      }
    }
  };

  const getGridOptions = (periodTitle: string) => {
    let columns: any[] = [
      {
        field: 'periodName',
        title: periodTitle,
        width: 180,
        locked: true,
        sortable: true,
      },
    ];

    let series = chartSetup?.series;

    if (series) {
      for (let i = 0; i < series.length; i++) {
        let serie = series[i];
        let measure = boardData?.measures?.find((m) => m.id?.toString() === serie.measureId?.toString());
        if (!measure) continue;

        let nodeColumns: any[] = columns;

        if (series.length > 1) {
          let column: any = {
            title: serie.name,
            headerAttributes: {
              style: 'text-align: center',
            },
            columns: [],
          };
          columns.push(column);
          nodeColumns = column.columns;
        }

        let focusedNodes = focusedNode?.childNodes || nodes;

        if (focusedNodes) {
          for (let j = 0; j < focusedNodes.length; j++) {
            let node = focusedNodes[j];
            let field = '_' + node.id + '_' + serie.measureId;

            nodeColumns.push({
              field: field,
              title: node.name,
              width: 200,
              headerAttributes: {
                style: 'text-align: right',
              },
              attributes: {
                style: 'text-align: right',
              },
              node: node,
              measure: measure,
            });
          }
        }
      }
    }

    clearEmptyGridColumns(columns);

    return {
      dataSource: {
        data: gridData,
        sort: { field: 'endDate', dir: 'asc' },
      },
      columns: columns,
      excel: {
        fileName: widget.name + '.xlsx',
      },
      excelExport: onExcelExport,
    };
  };

  const drillDown = (nodeId: number) => {
    if (popupVisible) {
      setPopUpVisible(false);
    }

    if (selectedObject) {
      let node = findNode(selectedObject.nodes || [], nodeId);

      if (node) {
        setFocusedNode(node);

        const parentNodes = defineNodesForParent(selectedObject, node.id || 0);

        if (chartSetup?.showParentOnDrillDown) {
          const compareNode = node.isGroup ? node.parentNode : node;
          if (parentNodes) {
            let newNodes = [...parentNodes];
            if (compareNode) {
              newNodes.push(compareNode);
            }
            setNodes(newNodes);
          }
        }

        if (selectedPeriod) {
          fetchNodeData(widget, parentNodes || [], getChartMeasures(chartSetup), selectedPeriod, localCurrency?.currency || '', false)
            .then((data) => {
              let appendParent = false;
              if (node?.parentNode && node?.parentNode.parentNode) {
                appendParent = !breadcrumbs.find((b) => b.id === node?.parentNode?.id);
              }
              setBreadcrumbs([
                ...breadcrumbs,
                {
                  id: node?.id || 0,
                  name: (appendParent ? node?.parentNode?.name + ' / ' : '') + node?.name,
                  level: breadcrumbs.length,
                  node: node,
                },
              ]);

              setChartData(data);
            });
        }
      }
    }
  };

  const handleSeriesClickCircle = (e): IChartCashflow | null => {
    if (!e.dataItem || !e.dataItem.lookupRef)
      return null;

    let ref = e.dataItem.lookupRef;
    //@ts-ignore
    let value = chartData.find((dataItem) => dataItem['nodeId'] === parseInt(ref.nodeId) && dataItem['measureId'] === parseInt(ref.measureId) && dataItem['periodId'] === selectedPeriod['id']);

    let measure = boardData?.measures?.find((m) => m.id === ref.measureId);

    let nodesForSearch = focusedNode?.childNodes || nodes;
    let node = nodesForSearch?.find((n) => n.id === ref.nodeId);

    if (value) {
      setDrillDownData(drillDownInfo(node?.id || 0));

      return {
        nodeId: node?.id,
        scenarioId: node?.scenarioId,
        valueId: value.id,
        value: e.dataItem.amount,
        percentage: e.dataItem.percentage,
        valuePercentage: e.dataItem?.percentage && value?.percentage && formatNumber(value.percentage, `p${e.dataItem?.lookupRef?.scale || 0}`),
        measureId: measure && measure.id,
        viewCashflows: chartSetup?.series && chartSetup.series[0].viewCashflows,
        currency: node?.currency,
        period: selectedPeriod,
        valueInfo: {
          name: node?.name,
          measure: measure,
          amount: e.dataItem.amount,
          scale: chartSetup?.series && chartSetup.series[0].scale,
        },
      };
    }

    return null;
  };

  const _replaceRowAmount = (field: string): string => {
    return field.replace(/_rowAmount$/, '');
  }

  const gridCellClick = (dataItem: any, field: string, measureId: number, nodeId: number) => {
    field = _replaceRowAmount(field);
    let measure = boardData?.measures?.find((m) => m.id && m.id.toString() === measureId.toString());

    let node = nodes?.find((n) => n.id === nodeId);

    //@ts-ignore
    let value = chartData.find((chartDataItem) => chartDataItem['nodeId'] === nodeId && chartDataItem['measureId'] === measureId && chartDataItem['periodId'] === dataItem.periodId);

    let period = chartSetup?.singlePeriod
      ? selectedPeriod
      : boardData?.timePeriods.find((p) => p.id === dataItem.periodId);

    setDrillDownData(drillDownInfo(node?.id || 0));
    setCashFlow({
      nodeId: node?.id,
      scenarioId: node?.scenarioId,
      valueId: value.id,
      value: dataItem,
      measureId: measureId,
      viewCashflows: true,
      period: period,
      valueInfo: {
        name: node?.name,
        measure: measure,
        amount: dataItem[field],
      },
    });
  };

  const seriesClick = (e) => {
    switch (chartSetup?.type) {
      case 'line':
      case 'bar':
      case 'column':
      case 'area':
      case 'marker':
      case 'funnel':
        setCashFlow(handleSeriesClickAxes(e));
        break;
      case 'pie':
      case 'donut':
        setCashFlow(handleSeriesClickCircle(e));
        break;
    }
  };

  const openCashFlowDialog = () => {
    if (popupVisible) {
      setPopUpVisible(false);
    }
    dispatch({
      type: MODAL.SWITCH_CASH_FLOW_MODAL,
      payload: {
        dataItem: {
          name: cashFlow.valueInfo.name,
          currency: cashFlow.currency,
        },
        cashFlowInfo: cashFlow,
        investorObjectId: widget.investorObjectId,
        columnName: cashFlow.valueInfo.measure.name,
        value: formatNumber(
          cashFlow.value.amount ? cashFlow.value.amount : cashFlow.valueInfo.amount,
          cashFlow.valueInfo.measure.isPercent ? `p${cashFlow.valueInfo.scale}` : `n${cashFlow.valueInfo.scale}`,
        ),
        boardId: params.boardId || boardData?.boardId,
        selectedCurrency: selectedCurrency,
      },
    });
  };

  const getChartMeasures = (setup?: IChartSetup): IBoardMeasure[] => {
    let chartMeasures: IBoardMeasure[] = [];

    setup?.series?.forEach((serie) => {
      const measure = boardData?.measures?.find((m) => m.id === serie.measureId);

      if (measure)
        chartMeasures.push(measure);
    });

    return chartMeasures;
  };

  const handleSeriesClick = (e) => {
    setPosition({ left: e.nativeEvent.x.location, top: e.nativeEvent.y.location });
    seriesClick(e);
    setPopUpVisible(true);
  };

  const handleLocalCurrencyChange = (e) => {
    setLocalCurrency(e.value);

    if (selectedPeriod && selectedObject) {
      setLoading(false);


      fetchData(widget, nodes, getChartMeasures(chartSetup), nodeGroups, selectedPeriod, e.value.currency || '', false)
        .then((data) => {
          setChartData(data);
          setLoading(false);
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
        });
    }
  };

  const drillToLevel = (breadcrumbNode: IChartBreadcrumb) => {
    let level: any;
    let levelNodes: any = [];

    if (breadcrumbNode.level === 0 && selectedObject && selectedPeriod) {
      setFocusedNode(null);

      if (chartSetup?.sliceGroup && level)
        levelNodes = defineNodesForCategoryTypeLevel(widget, chartSetup?.sliceGroup, level, selectedObject);
      else {
        let { nodes } = defineNodes(widget, selectedObject);
        levelNodes = nodes;
      }

      fetchNodeData(widget, levelNodes, boardData?.measures || [], selectedPeriod, localCurrency?.currency || '', false)
        .then((data) => {
          setBreadcrumbs([...breadcrumbs].slice(0, 1));
          setChartData(data);
        });
      return;
    }

    setFocusedNode(breadcrumbNode.node);

    if (selectedObject)
      levelNodes = defineNodesForParent(selectedObject, breadcrumbNode.node.id);

    if (chartSetup?.showParentOnDrillDown) {
      let compareNode = breadcrumbNode.node?.isGroup ? breadcrumbNode.node?.parentNode : breadcrumbNode.node;
      levelNodes.push(compareNode);
    }

    setBreadcrumbs([...breadcrumbs].slice(0, (breadcrumbNode.level ? breadcrumbNode.level : 0) + 1));

    if (selectedPeriod) {
      fetchNodeData(widget, levelNodes, boardData?.measures || [], selectedPeriod, localCurrency?.currency || '', false)
        .then((data) => {
          setChartData(data);
        });
    }
  };

  const handleChartClick = (e, dataItem: any, node: IBoardObjectNode, measure: IBoardMeasure, field: string) => {
    setPosition({ left: e.pageX, top: e.pageY });
    gridCellClick(dataItem, field, measure.id || 0, node.id || 0);
    setPopUpVisible(true);
  };

  const setWidgetExpand = (expand: boolean) => {
    setExpand(expand);
  };

  const filterNotes = (notes: IBoardNote[]) => {
    let chartNotes: IBoardNote[] = [];
    const currentColumns = chartSetup?.series;

    if (currentColumns) {
      for (let i = 0; i < notes.length; i++) {
        const measures = notes[i] && notes[i].measures;
        if (measures) {
          for (let j = 0; j < measures.length; j++) {
            let measureId = measures[j].measureId;
            currentColumns.forEach((column) => {
              if (column.measureId === measureId && !chartNotes.includes(notes[i])) {
                chartNotes.push(notes[i]);
              }
            });
          }
        }
      }
    }

    setChartNotes(chartNotes);
  };

  const clickOutsideHandler = () => {
    setPopUpVisible(false);
  };

  const CashflowCellRender = (columnName, node, measure, dataItem, field) => {
    return (props) => <ChartCashflowCell {...props} chartClick={handleChartClick} node={node} measure={measure}
                                         dataItem={dataItem} field={field} />;
  };

  const ChartMemo = useMemo(() => {
    let seriesWithoutZeroes = series?.map((serie) => {
      let filteredData = serie.data?.filter((s) => s.value !== 0);
      return { ...serie, data: filteredData };
    });
    return <ChartRender series={seriesWithoutZeroes} setup={chartSetup} categories={categories}
                        valueAxis={valueAxes} handleSeriesClick={handleSeriesClick}
                        ref={chartRef} />;
  }, [series, chartData, loading, expand]);

  const GridMemo = useMemo(() => (
    <div className={'grid-widget-table chart-grid '}>
      <ExcelExport data={orderBy(gridData, sort)} ref={exportRef} fileName={widget.name || t('EXPORT')}>
        {
          gridOptions.columns && gridOptions.columns.map((item, key) => {
            if (item.field === 'periodName')
              return <ExcelExportColumn field={item.field} key={key} title={item.title} />;
            if (item.columns) {
              return <ExcelExportColumnGroup title={item.title} key={key}>
                {
                  item.columns.map((child) => <ExcelExportColumn field={child.field}
                                                                 title={child.title} key={key} />)
                }
              </ExcelExportColumnGroup>;
            }
            return <ExcelExportColumn field={item.field} key={key} title={item.title} />;
          })
        }
        <Grid data={orderBy(gridData, sort)} className='numeric-table-style-wrapper' sortable={true}
              sort={sort} onSortChange={handleSortChange}
              ref={gridRef}>
          {
            gridOptions.columns &&
            gridOptions.columns.map((item, idx) => {
              return item.field === 'periodName' ? (
                <GridColumn field={item.field} title={item.title} width={250} key={idx} locked />
              ) : item.columns ? (
                <GridColumn
                  field={item.field}
                  title={item.title}
                  width={250}
                  key={idx}
                  cell={CashflowCellRender(item.name, item.node, item.measure, gridData, item.field)}>
                  {
                    item.columns &&
                    item.columns.map((nestedItem, i) => {
                      return (
                        <GridColumn
                          className='align-right'
                          field={nestedItem.field}
                          title={nestedItem.title}
                          width={250}
                          key={i}
                          format={`{0:${nestedItem.measure.isPercent ? 'p' : 'n'}${nestedItem.measure.scale}}`}
                        />
                      );
                    })
                  }
                </GridColumn>
              ) : (
                <GridColumn field={item.field} title={item.title} width={250} key={idx}
                            format={`${item.measure.isPercent ? 'p' : 'n'}${item.measure.scale}`}
                            cell={(props) => (<NumericCell {...props} />)} />
              );
            })
          }
        </Grid>
      </ExcelExport>
    </div>
  ), [series, chartData, loading, gridData, gridOptions]);

  useClickOutside(popupWrapperRef, clickOutsideHandler, 'series-click-button');

  useEffect(() => {
    if (chartItemPicker && chartItemPicker.type) {
      switch (chartItemPicker.type) {
        case 'svg':
          onSVGExportClick();
          break;
        case 'image':
          onImageExportClick();
          break;
        case 'pdf':
          onPDFExportClick();
          break;
        case 'excel':
          onExcelExport();
          break;
        case 'toggle':
          setGridView(!gridView);
          break;
        default:
          return;
      }
    }
  }, [chartItemPicker]);

  useEffect(() => {
    let wSetup = widgetSetup(widget);

    if (wSetup) {
      setChartSetup(wSetup);
    }

    setLocalCurrency(selectedCurrency);

    if (selectedObject && selectedPeriod && selectedCurrency && boardData) {
      const { nodes, groups } = defineNodes(widget, selectedObject);
      setNodes(nodes);
      setNodeGroups(groups);

      if (scenarios) {
        setWidgetCurrencies(generateCurrencies(widget, nodes, scenarios));
      }
      
      const widgetPromise = fetchData(widget, nodes, getChartMeasures(wSetup), groups, selectedPeriod, selectedCurrency.currency || '', false);
      widgetPromise
        .then((data) => {
          setChartData(data);
          setLoading(false);
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
        });
    }
  }, [selectedObject, selectedPeriod, selectedCurrency]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      initChart(chartData);
    }
    return () => {
      isMounted = false;
    };
  }, [chartData, focusedNode]);

  useEffect(() => {
    let isMounted = true;
    setGridOptions(getGridOptions(t('WIDGET.PERIOD_TITLE')));
    return () => {
      isMounted = false;
    };
  }, [gridData]);

  // useEffect(() => {
  //     setLocalCurrency(selectedCurrency);
  // }, [selectedCurrency]);

  useEffect(() => {
    if (notes) {
      filterNotes(notes);
    }
  }, [notes]);

  return (
    <WidgetWrapper widget={widget} localCurrency={localCurrency} widgetCurrencies={widgetCurrencies}
                   handleLocalCurrencyChange={handleLocalCurrencyChange} notes={chartNotes}
                   customisation={customisation} setWidgetExpand={setWidgetExpand} selectedObject={selectedObject}
                   moveWidget={moveWidget} removeWidget={removeWidget} handleUnpin={handleUnpinTrigger}
                   handleLink={handleLink} homeContent={homeContent} widgetView={widgetView} content={content}
                   handlePinCallback={handlePinWidget} handleUnpinFromBoardCallback={handleUnpinWidget}
                   unpinId={unpinId}
                   renderIcons={<Picker list={!gridView ? pickerListChart : pickerListGrid} field='name' justIcon
                                        iconName='fas fa-angle-down'
                                        handlePick={(item) => setChartItemPicker(item)} />}>
      {loading ? <ContentLoading /> : (
        <div className='chart-widget-wrapper'>
          <div className='charts-breadcrumbs'>
            {breadcrumbs.length > 1 &&
              breadcrumbs.map((breadcrumbNode) => (
                <button className='breadcrumb' onClick={() => drillToLevel(breadcrumbNode)}
                        key={breadcrumbNode.level}>
                  {breadcrumbNode.level === 0 ?
                    <Icon name='fas fa-backward-fast'
                          className='pointer' /> : ` / ${breadcrumbNode.name}   `}
                </button>
              ))}
          </div>
          <div className={`chart-wrapper ${breadcrumbs.length > 1 ? 'breadcrumbs-on' : ''}`}>
            {!gridView ? ChartMemo : GridMemo}
          </div>
          <div ref={popupWrapperRef}>
            <Popup offset={position} show={popupVisible} popupClass={'chart-popup-content'}
                   className={'chart-popup-wrapper'} animate={false}>
              <div className={'pointer close-popup-button'} onClick={() => setPopUpVisible(false)}>
                <Icon name={'fa-solid fa-circle-xmark'} />
              </div>
              {cashFlow && (
                <>
                  <div>{cashFlow?.valueInfo?.name}</div>
                  <div> {cashFlow?.period?.name}</div>
                  <div>
                    {cashFlow?.valueInfo?.measure?.name}:{' '}
                    {formatNumber(
                      cashFlow?.value?.amount ? cashFlow?.value?.amount : cashFlow?.valueInfo?.amount,
                      cashFlow?.valueInfo?.measure?.isPercent ? `p${cashFlow?.valueInfo?.scale}` : `n${cashFlow?.valueInfo?.scale}`,
                    )}
                  </div>
                  {cashFlow?.valuePercentage && <div>{cashFlow?.valuePercentage}</div>}
                  <div className={'break-line'} />
                  {cashFlow?.viewCashflows && (
                    <div
                      className={'series-click-button pointer'}
                      id={'series-click-button'}
                      onClick={() => openCashFlowDialog()}>
                      <div className='series-click-name'
                           onClick={(e) => e.stopPropagation()}>{t('WIDGET.VIEW_CASHFLOWS')}</div>
                      <Icon name={'fa-regular fa-eye'} onClick={(e) => e.stopPropagation()}
                            className='drilldown-icon' />
                    </div>
                  )}
                  {drillDownData &&
                    drillDownData.map((drillDownNode, idx) => (
                      <div className='series-click-button pointer' id={'series-click-button'}
                           onClick={() => drillDown(drillDownNode.id || 0)}
                           key={idx}>
                        <div
                          className='series-click-name'>{drillDownNode.name || t('CHART_WIDGET.DRILL_DOWN')}</div>
                        <Icon name={'fa-solid fa-split'} className='drilldown-icon' />
                      </div>
                    ))}
                </>
              )}
            </Popup>
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default ChartWidget;